import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { Chart, PatientDoc } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useRecordStripePayment, type RecordPaymentState } from '../../../../hooks/useRecordStripePayment';
import * as Sentry from '@sentry/browser';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';

export const Route = createFileRoute('/_authenticated/$patientId/payment/thanks')({
  component: Thanks,
});

function Thanks() {
  const { patientId } = Route.useParams();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();

  // THE SERVER DECIDES WHETHER THIS PATIENT PAID (CU-17tm466h63h). This page used to mark the
  // patient paid itself when its URL contained "succeeded", with the amount from the query string,
  // so typing the URL was enough. It now sends only the `payment_intent` id that Stripe appends to
  // the return URL; recordStripePayment asks Stripe and writes PAYMENT_INFO. Nothing here writes
  // the paid flag, and nothing reads an amount from the URL.
  const [paymentIntentId] = React.useState(() => new URLSearchParams(window.location.search).get('payment_intent'));
  const { state: payment, retry } = useRecordStripePayment(paymentIntentId);
  const chartPaid = chart?.PAYMENT_INFO?.alreadyPaid === true;
  const returnToPayment = () => navigate({ to: '/$patientId/payment/selfPay', params: { patientId } });

  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };
  return (
    <>
      <div id="thanks-page-container" className="flex h-screen flex-col items-center justify-center">
        {/* WHAT THE SERVER FOUND FOR THIS PAYMENT (CU-17tm466h63h) */}
        <PaymentOutcome
          payment={payment}
          chartPaid={chartPaid}
          chartLoaded={!!chart}
          onRetry={retry}
          onReturnToPayment={returnToPayment}
        />

        {/* SHOW DISCOUNT CODE APPLIED MESSAGE */}
        {payment.status === 'recorded' && (chart?.PAYMENT_INFO?.discountApplied ?? 0) > 0 && (
          <h1 className="text-xl">Discount Code Applied</h1>
        )}

        {/* SHOW SUCCESSFULLY PRE-PAYING CONCIERGE-GUIDED USERS A SUCCESS MESSAGE AND THE NEXT PAGE BUTTON TO CONTINUE TO INSURANCE OR MEDICARE */}
        {/* {nextPageURL &&
          succeeded &&
          patientInfo?.navType?.base === 'concierge' &&
          (chart?.PAYMENT_INFO.isPrePaidInsurance || chart?.PAYMENT_INFO?.isPrePaidMedicare) && (
            <div className="flex flex-col items-center justify-center text-center">
              <h1 className="text-xl">Payment applied. You may now continue to the next page</h1>
              <NavForward to={nextPageURL} />
            </div>
          )} */}
        {/* SHOW SUCCESSFULLY PAID CONCIERGE-GUIDED USERS A SUCCESS MESSAGE AND THE NEXT SECTION BUTTON */}
        {/* {nextPageURL &&
          patientInfo?.conciergeNavigator?.photosUnlocked === false &&
          chart?.PAYMENT_INFO.isPrePaidInsurance === false &&
          chart?.PAYMENT_INFO?.isPrePaidMedicare === false && (
            <p className="text-xl">Please wait for your concierge to unlock the next section</p>
          )} */}
        {/* {nextPageURL && patientInfo?.conciergeNavigator?.photosUnlocked && <NextSection to={nextPageURL} />} */}

        {/* SHOW SUCCESSFULLY PAID SELF-GUIDED USERS A SUCCESS MESSAGE AND THE NEXT PAGE BUTTON */}
        {patientInfo?.navType?.base === 'self' && chart?.PAYMENT_INFO?.alreadyPaid === true && (
          <>
            <h1 className="text-xl">You may now continue to the next page</h1>
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </>
        )}
        {/* SHOW CONCIERGE PATIENTS MESSAGE WITH OPTION TO CONTINUE */}
        {patientInfo?.navType?.base === 'concierge' && chart?.PAYMENT_INFO?.alreadyPaid === true && (
          <>
            <h1 className="text-xl">You may wait here for your concierge or click Next to continue.</h1>
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </>
        )}
        {/* {patientInfo?.navType.base === 'self' && nextPageURL && <NavForward to={nextPageURL} />} */}
      </div>
    </>
  );
}

const SUPPORT_EMAIL = 'info@mideohealth.com';
const panelButton = 'mt-4 rounded-md bg-sky-700 px-6 py-2 text-white hover:bg-sky-800';

/**
 * The patient-facing result of recordStripePayment. Every failure names a way
 * forward. ⚠️ A retryable failure may follow a REAL charge, so its copy says
 * not to pay again: going back to self-pay there could charge twice.
 */
function PaymentOutcome(props: {
  payment: RecordPaymentState;
  chartPaid: boolean;
  chartLoaded: boolean;
  onRetry: () => void;
  onReturnToPayment: () => void;
}) {
  const { payment } = props;
  const contact = (
    <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
      {SUPPORT_EMAIL}
    </a>
  );
  const returnButton = (
    <button id="payment-return" type="button" onClick={props.onReturnToPayment} className={panelButton}>
      Return to payment
    </button>
  );
  // "Check Again", never "Try Again": next to "please don't pay again", a patient
  // reads "try again" as "pay again". This button only asks the server again.
  const retryButton = (
    <button id="payment-retry" type="button" onClick={props.onRetry} className={panelButton}>
      Check Again
    </button>
  );
  const box = 'flex max-w-xl flex-col items-center justify-center p-4 text-center text-xl';

  switch (payment.status) {
    case 'idle':
      // No payment to confirm (e.g. the URL was typed). A patient already marked paid sees the Next button below.
      if (!props.chartLoaded || props.chartPaid) return null;
      return (
        <div id="payment-not-found" className={box}>
          <p>We couldn&apos;t find a payment to confirm.</p>
          {returnButton}
        </div>
      );
    case 'confirming':
      return (
        <div id="payment-confirming" className={box}>
          <p className="text-gray-600">Confirming your payment...</p>
          {payment.slow && (
            <p className="mt-2 text-base text-gray-600">This can take up to a minute. Please keep this page open.</p>
          )}
        </div>
      );
    case 'recorded':
      return (
        <div id="payment-recorded" className={box}>
          <p>Payment received.</p>
          <h1 className="mt-2 text-center">For a copy of your receipt please email {contact}</h1>
        </div>
      );
    case 'processing':
      return (
        <div id="payment-processing" className={box}>
          {/* Nothing re-checks a processing payment yet (CU-17tm466hkbz webhook), so promise nothing automatic. */}
          <p>Your payment is still processing.</p>
          <p className="mt-2 text-base">
            Please don&apos;t pay again. If your account isn&apos;t updated within 5 business days, contact {contact}.
          </p>
        </div>
      );
    case 'not-paid':
      return (
        <div id="payment-not-found" className={box}>
          <p>We couldn&apos;t confirm a payment.</p>
          <p className="mt-2 text-base">If you were charged, please contact {contact}.</p>
          {returnButton}
        </div>
      );
    case 'failed':
      switch (payment.kind) {
        case 'not-found':
          return (
            <div id="payment-not-found" className={box}>
              <p>We couldn&apos;t confirm a payment.</p>
              <p className="mt-2 text-base">If you were charged, please contact {contact}.</p>
              {returnButton}
            </div>
          );
        case 'refused':
          return (
            <div id="payment-refused" className={box}>
              <p>This payment belongs to a different account, so it can&apos;t be recorded here.</p>
            </div>
          );
        case 'unconfirmable':
          return (
            <div id="payment-unconfirmable" className={box}>
              <p>We received your payment but couldn&apos;t confirm it automatically.</p>
              <p className="mt-2 text-base">
                Please don&apos;t pay again. Contact {contact} and we&apos;ll update your account.
              </p>
            </div>
          );
        default:
          // retryable, offline, rate-limited: a retry can succeed, and the server write is idempotent.
          return (
            <div id="payment-failed" className={box}>
              <p>We&apos;re still confirming your payment.</p>
              <p className="mt-2 text-base">
                Please don&apos;t pay again.{' '}
                {payment.kind === 'offline'
                  ? 'You appear to be offline. Reconnect, then tap Check Again.'
                  : 'Wait a moment, then tap Check Again.'}{' '}
                If this keeps happening, contact {contact}.
              </p>
              {retryButton}
            </div>
          );
      }
  }
}
